import { PROPERTY_CARD_MAX_DOTS } from './property-card.util';

export const PROPERTY_CARD_DOT_TRANSITION_MS = 140;
export const PROPERTY_CARD_SWIPE_THRESHOLD_PX = 40;

export const PROPERTY_CARD_LEFT_ARROW_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M14.91 6.71a1 1 0 0 0 0 1.41L11.23 12l3.68 3.88a1 1 0 0 1-1.45 1.37l-4.41-4.65a1 1 0 0 1 0-1.37l4.41-4.65a1 1 0 0 1 1.45 1.37z" fill="currentColor"/></svg>';

export const PROPERTY_CARD_RIGHT_ARROW_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M9.09 6.71a1 1 0 0 1 0 1.41L12.77 12l-3.68 3.88a1 1 0 0 0 1.45 1.37l4.41-4.65a1 1 0 0 0 0-1.37l-4.41-4.65a1 1 0 0 0-1.45 1.37z" fill="currentColor"/></svg>';

type GalleryZone = 'start' | 'middle' | 'end';

export function propertyCardDotCount(photoCount: number): number {
  return Math.min(photoCount, PROPERTY_CARD_MAX_DOTS);
}

export function wrapPropertyPhotoIndex(index: number, photoCount: number): number {
  if (photoCount <= 0) {
    return 0;
  }

  return ((index % photoCount) + photoCount) % photoCount;
}

function galleryZone(photoIndex: number, photoCount: number): GalleryZone {
  if (photoCount <= PROPERTY_CARD_MAX_DOTS) {
    return 'start';
  }

  if (photoIndex <= 2) {
    return 'start';
  }

  if (photoIndex === photoCount - 1) {
    return 'end';
  }

  return 'middle';
}

export function propertyCardHomeDot(photoIndex: number, photoCount: number): number {
  if (photoCount <= PROPERTY_CARD_MAX_DOTS) {
    return photoIndex;
  }

  if (photoIndex <= 2) {
    return photoIndex;
  }

  if (photoIndex === photoCount - 1) {
    return 3;
  }

  return 2;
}

export function propertyCardPhotoStepDirection(
  previousPhotoIndex: number,
  currentPhotoIndex: number,
  photoCount: number,
): 'forward' | 'backward' | 'none' {
  if (previousPhotoIndex === currentPhotoIndex || photoCount <= 1) {
    return 'none';
  }

  const forwardSteps = (currentPhotoIndex - previousPhotoIndex + photoCount) % photoCount;
  const backwardSteps = (previousPhotoIndex - currentPhotoIndex + photoCount) % photoCount;
  return forwardSteps <= backwardSteps ? 'forward' : 'backward';
}

export function propertyCardDotAnimationSteps(
  previousPhotoIndex: number,
  currentPhotoIndex: number,
  photoCount: number,
): number[] {
  const prevZone = galleryZone(previousPhotoIndex, photoCount);
  const currZone = galleryZone(currentPhotoIndex, photoCount);
  const prevHome = propertyCardHomeDot(previousPhotoIndex, photoCount);
  const currHome = propertyCardHomeDot(currentPhotoIndex, photoCount);
  const forward = propertyCardPhotoStepDirection(previousPhotoIndex, currentPhotoIndex, photoCount) === 'forward';

  // Start zone: each photo maps to its own dot — switch directly, no loop pulse.
  if (prevZone === 'start' && currZone === 'start') {
    return [currHome];
  }

  if (prevZone === 'middle' && currZone === 'middle' && prevHome === currHome) {
    return forward ? [prevHome - 1, currHome] : [prevHome + 1, currHome];
  }

  if (forward && prevZone === 'start' && currZone === 'middle') {
    return [currHome - 1, currHome];
  }

  if (!forward && prevZone === 'middle' && currZone === 'start') {
    return [prevHome + 1, currHome];
  }

  return [prevHome, currHome];
}

export class PropertyCardDotAnimator {
  activeDot = 0;
  pulseDot: number | null = null;
  readonly dotCount: number;
  private transitionTimer?: ReturnType<typeof setTimeout>;
  private readonly onChange?: () => void;

  constructor(photoCount: number, initialPhotoIndex = 0, onChange?: () => void) {
    this.dotCount = propertyCardDotCount(photoCount);
    this.activeDot = propertyCardHomeDot(initialPhotoIndex, photoCount);
    this.onChange = onChange;
  }

  destroy(): void {
    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = undefined;
    }
  }

  applyTransition(
    previousPhotoIndex: number | null,
    currentPhotoIndex: number,
    photoCount: number,
    animate: boolean,
  ): void {
    this.destroy();
    const homeDot = propertyCardHomeDot(currentPhotoIndex, photoCount);

    if (!animate || previousPhotoIndex === null || previousPhotoIndex === currentPhotoIndex) {
      this.pulseDot = null;
      this.activeDot = homeDot;
      this.onChange?.();
      return;
    }

    this.runSequence(propertyCardDotAnimationSteps(previousPhotoIndex, currentPhotoIndex, photoCount));
  }

  private notifyChange(): void {
    this.onChange?.();
  }

  private runSequence(steps: number[]): void {
    if (steps.length === 0) {
      return;
    }

    if (steps.length === 1) {
      this.pulseDot = null;
      this.activeDot = steps[0];
      this.notifyChange();
      return;
    }

    let step = 0;

    const advance = () => {
      if (step < steps.length - 1) {
        this.pulseDot = steps[step];
        this.activeDot = -1;
        step += 1;
        this.notifyChange();
        this.transitionTimer = setTimeout(advance, PROPERTY_CARD_DOT_TRANSITION_MS);
        return;
      }

      this.pulseDot = null;
      this.activeDot = steps[step];
      this.transitionTimer = undefined;
      this.notifyChange();
    };

    advance();
  }
}

const DOT_ACTIVE_CLASS = 'property-card-dot--active';
const DOT_PREVIOUS_CLASS = 'property-card-dot--previous';

function clearDotStates(dots: Element[]): void {
  dots.forEach((dot) => {
    dot.classList.remove(DOT_ACTIVE_CLASS, DOT_PREVIOUS_CLASS);
  });
}

function cancelDotTransition(wrapper: HTMLElement): void {
  const timerId = wrapper.dataset['dotTransitionTimer'];
  if (!timerId) {
    return;
  }

  window.clearTimeout(Number(timerId));
  delete wrapper.dataset['dotTransitionTimer'];
}

function playDotSequence(wrapper: HTMLElement, steps: number[]): void {
  cancelDotTransition(wrapper);
  const dots = [...wrapper.querySelectorAll('.property-card-dot')];

  if (steps.length === 0) {
    return;
  }

  clearDotStates(dots);

  if (steps.length === 1) {
    dots[steps[0]]?.classList.add(DOT_ACTIVE_CLASS);
    return;
  }

  let step = 0;

  const runStep = () => {
    clearDotStates(dots);
    const dotIndex = steps[step];

    if (step < steps.length - 1) {
      dots[dotIndex]?.classList.add(DOT_PREVIOUS_CLASS);
      step += 1;
      const timer = window.setTimeout(runStep, PROPERTY_CARD_DOT_TRANSITION_MS);
      wrapper.dataset['dotTransitionTimer'] = String(timer);
      return;
    }

    dots[dotIndex]?.classList.add(DOT_ACTIVE_CLASS);
    delete wrapper.dataset['dotTransitionTimer'];
  };

  runStep();
}

export function updatePropertyCardDots(
  wrapper: HTMLElement,
  previousPhotoIndex: number | null,
  currentPhotoIndex: number,
  photoCount: number,
  animate: boolean,
): void {
  const homeDot = propertyCardHomeDot(currentPhotoIndex, photoCount);

  if (!animate || previousPhotoIndex === null || previousPhotoIndex === currentPhotoIndex) {
    cancelDotTransition(wrapper);
    const dots = [...wrapper.querySelectorAll('.property-card-dot')];
    clearDotStates(dots);
    dots[homeDot]?.classList.add(DOT_ACTIVE_CLASS);
    return;
  }

  playDotSequence(
    wrapper,
    propertyCardDotAnimationSteps(previousPhotoIndex, currentPhotoIndex, photoCount),
  );
}

export function isTouchGalleryDevice(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }

  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

export type PropertyCardWindowedSlideRole = 'prev' | 'current' | 'next';

export interface PropertyCardWindowedSlide {
  role: PropertyCardWindowedSlideRole;
  photoIndex: number | null;
}

export type PropertyCardWindowedTrackPosition = 'prev' | 'center' | 'next';

/** Indices for the three mounted touch-gallery slides (current ± 1). */
export function propertyCardWindowedSlides(
  currentIndex: number,
  photoCount: number,
): PropertyCardWindowedSlide[] {
  if (photoCount <= 0) {
    return [
      { role: 'prev', photoIndex: null },
      { role: 'current', photoIndex: null },
      { role: 'next', photoIndex: null },
    ];
  }

  const safeIndex = wrapPropertyPhotoIndex(currentIndex, photoCount);
  return [
    { role: 'prev', photoIndex: safeIndex > 0 ? safeIndex - 1 : null },
    { role: 'current', photoIndex: safeIndex },
    {
      role: 'next',
      photoIndex: safeIndex < photoCount - 1 ? safeIndex + 1 : null,
    },
  ];
}

/** Translate percent for a 3-slide track (each slide is one third of the track). */
export function propertyCardWindowedTrackPercent(position: PropertyCardWindowedTrackPosition): number {
  if (position === 'prev') {
    return 0;
  }

  if (position === 'next') {
    return -200 / 3;
  }

  return -100 / 3;
}

export function attachPropertyCardTouchSwipe(
  imageSection: HTMLElement,
  wrapper: HTMLElement,
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
): void {
  if (!isTouchGalleryDevice()) {
    return;
  }

  imageSection.classList.add('property-card-image--touch');

  let startX = 0;
  let startY = 0;
  let tracking = false;

  wrapper.addEventListener(
    'click',
    (event) => {
      const suppressUntil = Number(wrapper.dataset['suppressClickUntil'] ?? 0);
      if (Date.now() < suppressUntil) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    true,
  );

  imageSection.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length !== 1) {
        return;
      }

      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      tracking = true;
    },
    { passive: true },
  );

  imageSection.addEventListener(
    'touchmove',
    (event) => {
      if (!tracking || event.touches.length !== 1) {
        return;
      }

      const dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        event.stopPropagation();
      }
    },
    { passive: true },
  );

  imageSection.addEventListener(
    'touchend',
    (event) => {
      if (!tracking) {
        return;
      }

      tracking = false;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (Math.abs(dx) < PROPERTY_CARD_SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) {
        return;
      }

      event.stopPropagation();
      wrapper.dataset['suppressClickUntil'] = String(Date.now() + 400);

      if (dx < 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    },
    { passive: true },
  );
}
